import React, { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DetailedUserAchievement, Profile } from '@par/shared/types/player';
import { LatestGames } from './LatestGames';
import { LifetimeStats } from './LifetimeStats';
import { ProfileAvatar } from './ProfileAvatar';
import { ProfileAchievementsTable } from './ProfileAchievementsTable';
import { Aves } from './Aves';
import { Ratings } from './Ratings';
import { StyledPaperItem } from '../../common/mui-components';
import { Precision } from './Precision';
import { BiGhost } from 'react-icons/bi';
import {
  fetchAchievements,
  fetchProfileById,
} from '@par/shared/components/profile';
import { useParams } from 'react-router-dom';

type GhostChallengePlayer = {
  profileId: string;
  username: string;
  ghostChallengeStatus: string;
};

type PropTypes = {
  handleGhostChallenge: (player: GhostChallengePlayer) => void;
  onUpdateProfile: (updatedUsername?: string) => void;
  profile: Profile;
};

export const ProfileMain = ({
  handleGhostChallenge,
  onUpdateProfile,
  profile,
}: PropTypes) => {
  const theme = useTheme();
  const { profileId } = useParams();
  const [viewProfile, setViewProfile] = useState<Profile>();
  const [detailedUserAchievements, setDetailedUserAchievements] =
    useState<DetailedUserAchievement[]>();

  useEffect(() => {
    if (profileId) fetchProfilePageData(profileId);
  }, [profileId]);

  // While viewing your own profile, `viewProfile` is loaded once by id; after Edit Profile
  // saves, only `profile` in App is refetched — keep the page in sync without a refresh.
  useEffect(() => {
    if (!profileId || !profile) return;
    if (profile.id === profileId) {
      setViewProfile(profile);
    }
  }, [profile, profileId]);

  const fetchProfilePageData = async (profileId: string) => {
    const profileRes: Profile = await fetchProfileById(profileId);
    setViewProfile(profileRes);
    const achievements: DetailedUserAchievement[] = await fetchAchievements(
      profileId
    );
    setDetailedUserAchievements(achievements);
  };

  const ghostChallengeStatusTooltip = (status: 'yes' | 'no' | 'maybe') => {
    let message = 'May or may not respond to challenges';
    if (status === 'yes') {
      message = 'Send me challenges!';
    }
    if (status === 'no') {
      message = 'Not accepting challenges right now';
    }
    return <Typography variant="caption">Status: {message}</Typography>;
  };

  const handleGhostChallengeClick = (viewProfile: Profile) => {
    if (viewProfile.ghostChallengeStatus === 'no') return;
    const player: GhostChallengePlayer = {
      profileId: viewProfile.id,
      username: viewProfile.username,
      ghostChallengeStatus: viewProfile.ghostChallengeStatus,
    };
    handleGhostChallenge(player);
  };

  const ghostBorderColor = () =>
    viewProfile.ghostChallengeStatus === 'yes'
      ? theme.palette.underPar.main
      : viewProfile.ghostChallengeStatus === 'no'
      ? 'gray'
      : theme.palette.primary.main;

  return (
    <>
      <Container
        sx={{
          flexGrow: 1,
          marginTop: 2,
          paddingBottom: '100px',
          paddingLeft: 0,
        }}
      >
        {viewProfile ? (
          <Stack spacing={2} alignItems={'flex-start'}>
            <Stack
              className="row-profile-stats"
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              width={{ sm: '100%' }}
              flex={1}
            >
              {/* Profile */}
              <StyledPaperItem sx={{ flex: 1 }}>
                <ProfileAvatar
                  onUpdateProfile={onUpdateProfile}
                  profile={profile}
                  viewProfile={viewProfile}
                ></ProfileAvatar>
              </StyledPaperItem>

              <Stack direction={'column'} spacing={2} sx={{ flex: 2 }}>
                <Stack
                  className="row-rank-showcase-ghost"
                  direction={{ xs: 'column-reverse', sm: 'row' }}
                  spacing={2}
                >
                  {/* Ranked */}
                  <StyledPaperItem sx={{ flex: 1 }}>
                    <Ratings viewProfile={viewProfile}></Ratings>
                  </StyledPaperItem>

                  <Stack spacing={2} flex={1} className="col-ghost-showcase">
                    {/* Ghost Challenge Button */}
                    {profile.id !== viewProfile.id ? (
                      <StyledPaperItem>
                        <Tooltip
                          title={ghostChallengeStatusTooltip(
                            viewProfile.ghostChallengeStatus
                          )}
                          placement="top"
                        >
                          <Button
                            variant="outlined"
                            size="large"
                            fullWidth
                            sx={{
                              ':hover': {
                                borderColor: ghostBorderColor,
                              },
                              borderColor: ghostBorderColor,
                            }}
                            onClick={() =>
                              handleGhostChallengeClick(viewProfile)
                            }
                          >
                            <BiGhost size={'24px'} color={ghostBorderColor()} />
                            <Typography
                              ml={1}
                              color={ghostBorderColor}
                              fontWeight={'bold'}
                            >
                              Challenge!
                            </Typography>
                          </Button>
                        </Tooltip>
                      </StyledPaperItem>
                    ) : null}

                    {/* Showcases */}
                    <StyledPaperItem sx={{ height: '100%', flex: 1 }}>
                      <ProfileAchievementsTable
                        detailedUserAchievements={detailedUserAchievements}
                        onUpdateProfile={onUpdateProfile}
                        profile={profile}
                        viewProfile={viewProfile}
                      ></ProfileAchievementsTable>
                    </StyledPaperItem>
                  </Stack>
                </Stack>

                {/* Lifetime Stats */}
                <StyledPaperItem>
                  <LifetimeStats viewProfile={viewProfile}></LifetimeStats>
                </StyledPaperItem>
              </Stack>
            </Stack>

            {/* Aves */}
            <StyledPaperItem sx={{ width: '100%' }}>
              <Aves viewProfile={viewProfile}></Aves>
            </StyledPaperItem>

            {/* Precision */}
            <StyledPaperItem sx={{ width: '100%' }}>
              <Precision viewProfile={viewProfile}></Precision>
            </StyledPaperItem>

            {/* Latest Games */}
            <StyledPaperItem sx={{ width: '100%' }}>
              <LatestGames
                profile={profile}
                viewProfile={viewProfile}
              ></LatestGames>
            </StyledPaperItem>
          </Stack>
        ) : null}
      </Container>
    </>
  );
};
